"use client"

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react"
import { useSupabase } from "@/lib/supabase/context"
import { type SubscriptionPlan } from "@/lib/subscription-utils"

interface UserData {
  user: any
  userPlan: SubscriptionPlan
  subscriptionStatus: "active" | "canceled" | "past_due" | "trialing" | "grace_period"
  gracePeriodEnd: string | null
}

interface PlannerCacheContextType {
  userData: UserData | null
  isLoading: boolean
  refreshUserData: () => Promise<void>
}

const PlannerCacheContext = createContext<PlannerCacheContextType | undefined>(undefined)

export function PlannerCacheProvider({ children }: { children: ReactNode }) {
  const supabase = useSupabase()
  const [userData, setUserData] = useState<UserData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isLoadingRef = useRef(false)
  const retryCountRef = useRef(0)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastLoadAttemptRef = useRef<number>(Date.now())
  const isLoggedOutRef = useRef(false) // Track if user is logged out to stop retries
  const stuckCheckIntervalRef = useRef<NodeJS.Timeout | null>(null) // Ref for stuck check interval
  const maxRetries = 3
  const retryDelay = 1000 // Start with 1 second

  const isDemoModeUrl = useCallback(() => {
    return typeof window !== "undefined" && new URLSearchParams(window.location.search).get("demo") === "true"
  }, [])

  // Helper to check if error is auth-related (user logged out)
  const isAuthError = (error: any): boolean => {
    if (!error) return false
    const errorMessage = (error.message || error.toString() || '').toLowerCase()
    const errorName = error.name || ''

    // Only treat explicit auth errors as "logged out" conditions.
    // This avoids misclassifying generic 403/Forbidden or other errors as auth problems,
    // which could incorrectly force users back to the login screen.
    return (
      errorName === 'AuthSessionMissingError' ||
      errorName === 'AuthApiError' ||
      errorMessage.includes('auth session missing') ||
      errorMessage.includes('authsessionmissingerror') ||
      errorMessage.includes('invalid refresh token') ||
      errorMessage.includes('refresh token not found')
    )
  }

  // Stop all retries and clear state
  const stopAllRetries = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
    if (stuckCheckIntervalRef.current) {
      clearInterval(stuckCheckIntervalRef.current)
      stuckCheckIntervalRef.current = null
    }
    retryCountRef.current = 0
    isLoadingRef.current = false
    isLoggedOutRef.current = true
  }, [])
  
  // Start/stop stuck check interval based on state
  // Accepts current state values to avoid stale closures
  const manageStuckCheck = useCallback((currentUserData: UserData | null, currentIsLoading: boolean) => {
    // Don't run if logged out
    if (isLoggedOutRef.current) {
      if (stuckCheckIntervalRef.current) {
        clearInterval(stuckCheckIntervalRef.current)
        stuckCheckIntervalRef.current = null
      }
      return
    }
    
    // Only run interval if we don't have user data and we're not loading
    if (!currentUserData && !currentIsLoading) {
      // Start interval if not already running
      if (!stuckCheckIntervalRef.current) {
        stuckCheckIntervalRef.current = setInterval(() => {
          // Don't check if user is logged out
          if (isLoggedOutRef.current) {
            if (stuckCheckIntervalRef.current) {
              clearInterval(stuckCheckIntervalRef.current)
              stuckCheckIntervalRef.current = null
            }
            return
          }
          
          // Read latest values from refs (they're kept in sync)
          const checkUserData = userDataRef.current
          const checkIsLoading = isLoadingRef.current
          const timeSinceLastAttempt = Date.now() - lastLoadAttemptRef.current
          
          // Only retry if we're in a stuck state
          if (!checkIsLoading && !checkUserData && !isLoadingRef.current && timeSinceLastAttempt > 5000) {
            console.log("Detected stuck state (no userData after load completed), retrying")
            retryCountRef.current = 0
            lastLoadAttemptRef.current = Date.now()
            loadUserDataRef.current(true)
          } else if (checkUserData) {
            // We have user data, stop the interval
            if (stuckCheckIntervalRef.current) {
              clearInterval(stuckCheckIntervalRef.current)
              stuckCheckIntervalRef.current = null
            }
          }
        }, 10000) // Check every 10 seconds
      }
    } else if (currentUserData && stuckCheckIntervalRef.current) {
      // We have user data, stop the interval
      clearInterval(stuckCheckIntervalRef.current)
      stuckCheckIntervalRef.current = null
    }
  }, [])

  const loadUserData = useCallback(async (force = false) => {
    if (isDemoModeUrl()) {
      setUserData(null)
      setIsLoading(false)
      isLoadingRef.current = false
      stopAllRetries()
      return
    }

    // Don't retry if user is logged out
    if (isLoggedOutRef.current && !force) {
      return
    }
    
    // Prevent duplicate concurrent loads unless forced
    if (isLoadingRef.current && !force) return
    
    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
    
    isLoadingRef.current = true
    setIsLoading(true)
    lastLoadAttemptRef.current = Date.now()
    
    try {
      const {
        data: { user: currentUser },
        error: authError,
      } = await supabase.auth.getUser()
      
      if (authError) {
        // Check if it's an auth session error (user logged out)
        if (isAuthError(authError)) {
          // Silently handle missing sessions - completely normal for unauthenticated users
          isLoggedOutRef.current = true
          setUserData(null)
          setIsLoading(false)
          isLoadingRef.current = false
          stopAllRetries()
          // Redirect to sign-in if on planner page
          if (typeof window !== 'undefined' && window.location.pathname.startsWith('/planner') && !isDemoModeUrl()) {
            const currentUrl = window.location.pathname + window.location.search
            window.location.href = `/auth/login?returnUrl=${encodeURIComponent(currentUrl)}`
          }
          return
        }
        throw authError
      }
      
      // User is authenticated, reset logout flag
      isLoggedOutRef.current = false
      
      if (currentUser) {
        // Load subscription
        const { data: subData, error: subError } = await supabase
          .from("subscriptions")
          .select("plan_name, status, current_period_end, grace_period_end")
          .eq("user_id", currentUser.id)
          .maybeSingle()

        if (subError) {
          console.warn("Error loading subscription, using defaults:", subError)
        }

        setUserData({
          user: currentUser,
          userPlan: (subData?.plan_name as SubscriptionPlan) || "free",
          subscriptionStatus: (subData?.status as any) || "active",
          gracePeriodEnd: subData?.grace_period_end || null,
        })
        // Reset retry count on success
        retryCountRef.current = 0
      } else {
        setUserData(null)
        retryCountRef.current = 0
      }
    } catch (error) {
      // Check if it's an auth session error (user logged out)
      if (isAuthError(error)) {
        console.log("Auth session missing - user logged out, stopping retries")
        isLoggedOutRef.current = true
        setUserData(null)
        setIsLoading(false)
        isLoadingRef.current = false
        stopAllRetries()
        // Redirect to sign-in if on planner page
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/planner') && !isDemoModeUrl()) {
          const currentUrl = window.location.pathname + window.location.search
          window.location.href = `/auth/login?returnUrl=${encodeURIComponent(currentUrl)}`
        }
        return
      }
      
      console.error("Error loading user data:", error)
      
      // Don't retry if user is logged out
      if (isLoggedOutRef.current) {
        return
      }
      
      // Retry with exponential backoff if we haven't exceeded max retries
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current += 1
        const delay = retryDelay * Math.pow(2, retryCountRef.current - 1)
        console.log(`Retrying loadUserData in ${delay}ms (attempt ${retryCountRef.current}/${maxRetries})`)
        
        retryTimeoutRef.current = setTimeout(() => {
          loadUserData(true)
        }, delay)
      } else {
        // Max retries exceeded, stop loading but keep trying in background
        console.error("Max retries exceeded for loadUserData")
        retryCountRef.current = 0
      }
    } finally {
      setIsLoading(false)
      isLoadingRef.current = false
    }
  }, [supabase, stopAllRetries, isDemoModeUrl])

  // Use refs for event handlers to avoid recreating them on every render
  const loadUserDataRef = useRef(loadUserData)
  const userDataRef = useRef(userData)
  
  // Keep refs in sync
  useEffect(() => {
    loadUserDataRef.current = loadUserData
    userDataRef.current = userData
  }, [loadUserData, userData])

  useEffect(() => {
    loadUserData()
    
    // Listen to auth state changes to detect logout
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: string, session: any) => {
      if (isDemoModeUrl()) return

      if (event === 'SIGNED_OUT' || !session) {
        console.log("User signed out, stopping all retries")
        isLoggedOutRef.current = true
        setUserData(null)
        setIsLoading(false)
        stopAllRetries()
        // Redirect to sign-in if on planner page
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/planner') && !isDemoModeUrl()) {
          const currentUrl = window.location.pathname + window.location.search
          window.location.href = `/auth/login?returnUrl=${encodeURIComponent(currentUrl)}`
        }
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // User signed in or session refreshed, but verify session is valid before reloading
        if (session) {
          console.log("User signed in or session refreshed, reloading user data")
          isLoggedOutRef.current = false
          retryCountRef.current = 0
          loadUserDataRef.current(true)
        } else {
          // TOKEN_REFRESHED event fired but no session - user is logged out
          console.log("Token refresh event but no session - user logged out")
          isLoggedOutRef.current = true
          setUserData(null)
          setIsLoading(false)
          stopAllRetries()
          if (typeof window !== 'undefined' && window.location.pathname.startsWith('/planner') && !isDemoModeUrl()) {
            const currentUrl = window.location.pathname + window.location.search
            window.location.href = `/auth/login?returnUrl=${encodeURIComponent(currentUrl)}`
          }
        }
      }
    })
    
    // Listen for storage events (cache clears in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      // Don't reload if user is logged out
      if (isLoggedOutRef.current) return
      
      // Only reload if localStorage was completely cleared (e.key === null)
      // Don't reload on module data cache updates (travel_trips_, financial_goals_, wishes_, etc.)
      // Module data cache keys follow patterns like: planner_cache_travel_trips_*, planner_cache_financial_goals_*, etc.
      if (e.key === null) {
        console.log("Cache cleared, reloading user data")
        retryCountRef.current = 0
        loadUserDataRef.current(true)
      }
      // Note: We intentionally ignore module data cache updates (planner_cache_*) to prevent
      // unnecessary reloads when saving planner module data (travel, financial goals, etc.)
    }
    
    // Listen for visibility changes (page regains focus) - more efficient than focus event
    const handleVisibilityChange = () => {
      // Don't reload if user is logged out
      if (isLoggedOutRef.current) return
      
      // Only check when page becomes visible and we might need to reload
      if (document.visibilityState === 'visible') {
        const currentUserData = userDataRef.current
        const currentIsLoading = isLoadingRef.current
        
        // Only retry if we don't have user data and we're not currently loading
        if (!currentUserData && !currentIsLoading) {
          console.log("Page visible, checking user data")
          retryCountRef.current = 0
          loadUserDataRef.current(true)
        }
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      subscription.unsubscribe()
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopAllRetries()
    }
  }, [supabase, stopAllRetries]) // Minimal dependencies - only recreate when supabase changes
  
  // Separate effect to manage stuck check interval based on state
  // This only runs when userData or isLoading changes, not on every render
  useEffect(() => {
    manageStuckCheck(userData, isLoading)
  }, [userData, isLoading, manageStuckCheck])

  const refreshUserData = useCallback(async () => {
    retryCountRef.current = 0
    await loadUserData(true)
  }, [loadUserData])

  return (
    <PlannerCacheContext.Provider value={{ userData, isLoading, refreshUserData }}>
      {children}
    </PlannerCacheContext.Provider>
  )
}

export function usePlannerCache() {
  const context = useContext(PlannerCacheContext)
  if (context === undefined) {
    throw new Error("usePlannerCache must be used within PlannerCacheProvider")
  }
  return context
}
